import type { Time } from "@suse-o11y/date-time/time";
import { hasProperty } from "@suse-o11y/has-property";
import type { Record } from "@suse-o11y/record";
import { theme } from "@suse-o11y/twin-migration";

import { Api, type EventCategory, type TimelineSummaryEventBucket } from "../../../common/openapi";
import type { CategorizedEventBucket } from "../../../queries";
import type { GroupProps } from "../../_canvas";

export type TimelineEventCategory = "*" | EventCategory;

export const TIMELINE_EVENT_CATEGORY_PROPERTIES: Record<
	TimelineEventCategory,
	{
		label: string;
		mainColor: string;
		hoveredColor: string;
		selectedColor?: string;
	}
> = {
	"*": {
		label: "All events",
		mainColor: theme("colors.pastel.blue"),
		hoveredColor: theme("colors.blue.500"),
		selectedColor: theme("colors.blue.800"),
	},
	[Api.EventCategory.Anomalies]: {
		label: "Anomalies",
		mainColor: theme("colors.red.500"),
		hoveredColor: theme("colors.red.700"),
	},
	[Api.EventCategory.Alerts]: {
		label: "Alerts",
		mainColor: theme("colors.pink.500"),
		hoveredColor: theme("colors.pink.700"),
	},
	[Api.EventCategory.Activities]: {
		label: "Activities",
		mainColor: theme("colors.green.500"),
		hoveredColor: theme("colors.green.700"),
	},
	[Api.EventCategory.Changes]: {
		label: "Changes",
		mainColor: theme("colors.blue.500"),
		hoveredColor: theme("colors.blue.700"),
	},
	[Api.EventCategory.Deployments]: {
		label: "Deployments",
		mainColor: theme("colors.purple.400"),
		hoveredColor: theme("colors.purple.600"),
	},
	[Api.EventCategory.Others]: {
		label: "Other events",
		mainColor: theme("colors.graphite.500"),
		hoveredColor: theme("colors.graphite.700"),
	},
};

export interface CommonTimelineBucketProps<TBucket> extends Pick<GroupProps, "x" | "y" | "height" | "width"> {
	listening?: boolean;
	buckets: undefined | ReadonlyArray<TBucket>;
	minBucketHeight?: number;
	maxBucketHeight?: number;
}

export type TimelineEventBucket = TimelineSummaryEventBucket | CategorizedEventBucket;

const isTimelineSummaryEventBucket = (bucket: TimelineEventBucket): bucket is TimelineSummaryEventBucket =>
	hasProperty(bucket, "_type") && bucket._type === Api.TimelineSummaryEventBucket.TypeEnum.TimelineSummaryEventBucket;

export const getTimelineEventBucketStartTime = (bucket: TimelineEventBucket): Time => {
	if (isTimelineSummaryEventBucket(bucket)) {
		return bucket.startTimeEpochMillis;
	}

	return bucket.startTime;
};

export const getTimelineEventBucketEndTime = (bucket: TimelineEventBucket): undefined | Time => {
	if (isTimelineSummaryEventBucket(bucket)) {
		return bucket.endTimeEpochMillis;
	}

	return bucket.endTime;
};
